import { useEffect, useState } from "react";
import type { Lang } from "../lib/i18n";
import { pick } from "../lib/i18n";
import { adminExists, adminSetup, adminLogin, adminForgotPassword, adminResetPassword } from "../lib/api";

type Mode = "loading" | "login" | "setup" | "forgot" | "reset";

/** Admin sign-in / first-time setup / forgot-password form. */
export function AdminLogin({ lang, onSuccess, onExit }: { lang: Lang; onSuccess: () => void; onExit: () => void }) {
  const t = (ar: string, en: string) => pick(lang, ar, en);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<Mode>("loading");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const exists = await adminExists();
      if (!cancelled) setMode(exists ? "login" : "setup");
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  function goTo(next: Mode) {
    setError("");
    setNotice("");
    setPassword("");
    setConfirm("");
    setCode("");
    setMode(next);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setNotice("");

    if (mode === "setup") {
      if (!name.trim()) return setError(t("يرجى إدخال الاسم", "Please enter your name"));
      if (!email.trim()) return setError(t("يرجى إدخال بريد إلكتروني", "Please enter an email address"));
      if (password.length < 8) return setError(t("كلمة المرور يجب أن تكون 8 أحرف على الأقل", "Password must be at least 8 characters"));
      if (password !== confirm) return setError(t("كلمتا المرور غير متطابقتين", "Passwords do not match"));
      setBusy(true);
      const res = await adminSetup(name.trim(), email.trim(), password);
      setBusy(false);
      res.ok ? onSuccess() : setError(res.error || t("تعذّر إعداد حساب الأدمن", "Failed to set up admin"));
      return;
    }

    if (mode === "login") {
      setBusy(true);
      const res = await adminLogin(email.trim(), password);
      setBusy(false);
      res.ok ? onSuccess() : setError(res.error || t("بيانات الدخول غير صحيحة", "Invalid credentials"));
      return;
    }

    if (mode === "forgot") {
      if (!email.trim()) return setError(t("يرجى إدخال بريدك الإلكتروني", "Please enter your email"));
      setBusy(true);
      const res = await adminForgotPassword(email.trim());
      setBusy(false);
      if (res.ok) {
        goTo("reset");
        setNotice(t("إذا كان البريد مسجّلاً، وصلك رمز مكوّن من 6 أرقام. أدخله أدناه مع كلمة المرور الجديدة.", "If that email is registered, a 6-digit code was sent to it. Enter it below along with your new password."));
      } else {
        setError(res.error || t("تعذّر الإرسال", "Failed to send"));
      }
      return;
    }

    if (mode === "reset") {
      if (!code.trim() || code.trim().length !== 6) return setError(t("يرجى إدخال الرمز المكوّن من 6 أرقام", "Please enter the 6-digit code"));
      if (password.length < 8) return setError(t("كلمة المرور يجب أن تكون 8 أحرف على الأقل", "Password must be at least 8 characters"));
      if (password !== confirm) return setError(t("كلمتا المرور غير متطابقتين", "Passwords do not match"));
      setBusy(true);
      const res = await adminResetPassword(email.trim(), code.trim(), password);
      setBusy(false);
      if (res.ok) {
        goTo("login");
        setNotice(t("تم تحديث كلمة المرور، يمكنك تسجيل الدخول الآن.", "Password updated, you can sign in now."));
      } else {
        setError(res.error || t("الرمز غير صحيح أو منتهي الصلاحية", "Incorrect or expired code"));
      }
      return;
    }
  }

  if (mode === "loading") {
    return (
      <div className="kd-admin-login-wrap" dir={lang === "ar" ? "rtl" : "ltr"}>
        <div className="kd-admin-login-card">
          <p>{t("جارٍ التحميل…", "Loading…")}</p>
        </div>
      </div>
    );
  }

  const isSetup = mode === "setup";
  const isForgot = mode === "forgot";
  const isReset = mode === "reset";

  const title = isSetup
    ? t("إعداد حساب الأدمن", "Set up admin account")
    : isForgot
      ? t("استعادة كلمة المرور", "Reset password")
      : isReset
        ? t("تعيين كلمة مرور جديدة", "Set a new password")
        : t("لوحة الإدارة", "Admin Dashboard");

  const sub = isSetup
    ? t("هذه هي المرة الأولى، أنشئ بيانات دخول الأدمن الآن.", "First-time setup, create your admin credentials now.")
    : isForgot
      ? t("أدخل بريدك الإلكتروني وسنرسل لك رمزاً لإعادة تعيين كلمة المرور.", "Enter your email and we'll send you a code to reset your password.")
      : isReset
        ? t("أدخل الرمز الذي وصلك بالبريد مع كلمة المرور الجديدة.", "Enter the code emailed to you along with your new password.")
        : t("يُرجى إدخال بيانات الدخول للوصول إلى لوحة التحكم.", "Please enter your credentials to access the dashboard.");

  return (
    <div className="kd-admin-login-wrap" dir={lang === "ar" ? "rtl" : "ltr"}>
      <form className="kd-admin-login-card" onSubmit={submit}>
        <div className="kd-admin-login-icon">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>
        <h1>{title}</h1>
        <p className="kd-admin-login-sub">{sub}</p>

        {isSetup && (
          <label>
            <span>{t("الاسم", "Name")}</span>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} autoFocus autoComplete="name" />
          </label>
        )}

        {(isSetup || mode === "login" || isForgot) && (
          <label>
            <span>{t("البريد الإلكتروني", "Email")}</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus={mode === "login" || isForgot} autoComplete="email" disabled={isReset} />
          </label>
        )}

        {isReset && (
          <>
            <label>
              <span>{t("البريد الإلكتروني", "Email")}</span>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </label>
            <label>
              <span>{t("الرمز المكوّن من 6 أرقام", "6-digit code")}</span>
              <input type="text" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} autoFocus className="kd-admin-otp-input" placeholder="000000" />
            </label>
          </>
        )}

        {(isSetup || mode === "login" || isReset) && (
          <label>
            <span>{isSetup || isReset ? t("كلمة المرور الجديدة", "New password") : t("كلمة المرور", "Password")}</span>
            <div className="kd-admin-pw-wrap">
              <input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete={isSetup || isReset ? "new-password" : "current-password"} />
              <button type="button" className="kd-admin-pw-toggle" onClick={() => setShowPw((s) => !s)}>
                {showPw ? t("إخفاء", "Hide") : t("إظهار", "Show")}
              </button>
            </div>
          </label>
        )}

        {(isSetup || isReset) && (
          <label>
            <span>{t("تأكيد كلمة المرور", "Confirm password")}</span>
            <div className="kd-admin-pw-wrap">
              <input type={showPw ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
            </div>
          </label>
        )}

        {notice && <div className="kd-admin-msg is-ok">{notice}</div>}
        {error && <div className="kd-admin-login-err">{error}</div>}

        <button type="submit" className="kd-btn-primary kd-btn-gold" disabled={busy}>
          {busy
            ? t("لحظة…", "Please wait…")
            : isSetup
              ? t("إنشاء الحساب والدخول", "Create account & sign in")
              : isForgot
                ? t("إرسال الرمز", "Send code")
                : isReset
                  ? t("تعيين كلمة المرور", "Set new password")
                  : t("دخول", "Sign in")}
        </button>

        {mode === "login" && (
          <button type="button" className="kd-admin-login-link" onClick={() => goTo("forgot")}>
            {t("نسيت كلمة المرور؟", "Forgot password?")}
          </button>
        )}
        {(isForgot || isReset) && (
          <button type="button" className="kd-admin-login-link" onClick={() => goTo("login")}>
            {t("العودة لتسجيل الدخول", "Back to sign in")}
          </button>
        )}

        <button type="button" className="kd-btn-secondary" onClick={onExit}>
          {t("عودة للرحلة", "Back to journey")}
        </button>
        {isSetup && (
          <small className="kd-admin-login-hint">
            {t("كلمة المرور يجب أن تكون 8 أحرف على الأقل. يمكنك إضافة مستخدمين آخرين لاحقاً من تبويب المستخدمين.", "Password must be at least 8 characters. You can add more admin users later from the Users tab.")}
          </small>
        )}
      </form>
    </div>
  );
}
